import { AgGridReact } from "ag-grid-react";
import { PlusIcon, Search } from "lucide-react";
import React, { useEffect, useState } from "react";
import { FiEdit } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import Modal from "../components/Modal";
import useUserManagementStore from "../stores/userManagementStore";

const UserManagement = () => {
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [customers, setCustomers] = useState([]); // To store list of customers
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    tenantRoles: [],
    username: "",
    password: "",
    isLoginEnabled: false,
    tenantId: "", // Add tenantId to formData
  });

  const { firstName, lastName, email, mobile, tenantRoles, username, password, isLoginEnabled, tenantId } =
    formData;

  const {
    users,
    availableRoles,
    fetchUsers,
    fetchAvailableRoles,
    addUser,
    updateUser,
    deleteUser,
    searchUser,
    isLoading,
    error,
    clearError,
  } = useUserManagementStore();

  const userRole = localStorage.getItem("role"); // Get user role from localStorage
  const userTenantId = localStorage.getItem("tenantId"); // Get tenantId for CustomerAdmin

  // Fetch customers (organizations) for SuperAdmin and users
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}api/customer/getcustomers`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch customers");
        }
        const data = await response.json();
        setCustomers(data.customers || []);
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    };

    if (userRole === "SuperAdmin") {
      fetchCustomers();
    }
    fetchUsers();
    // For CustomerAdmin, fetch roles for their tenantId
    if (userRole === "CustomerAdmin" && userTenantId) {
      fetchAvailableRoles(userTenantId);
    }
    return () => clearError();
  }, [fetchUsers, fetchAvailableRoles, clearError, userRole, userTenantId]);

  // Fetch roles when tenantId changes (for SuperAdmin)
  useEffect(() => {
    if (userRole === "SuperAdmin" && tenantId) {
      fetchAvailableRoles(tenantId);
    }
  }, [tenantId, userRole, fetchAvailableRoles]);

  const handleSearch = async (e) => {
    e.preventDefault();
    const value = e.target.value;
    setSearchTerm(value);

    const query = {
      firstName: value,
    };

    if (!value) return fetchUsers();
    await searchUser(query);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox" && name === "tenantRoles") {
      setFormData((prev) => ({
        ...prev,
        tenantRoles: checked
          ? [...prev.tenantRoles, value]
          : prev.tenantRoles.filter((role) => role !== value),
      }));
    } else if (type === "checkbox" && name === "isLoginEnabled") {
      setFormData((prev) => ({ ...prev, isLoginEnabled: checked }));
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (isLoginEnabled && (!username || (!password && !isEditing))) {
      alert("Username and password are required when login is enabled.");
      return;
    }

    // For CustomerAdmin, implicitly set tenantId from localStorage
    const submissionData = { ...formData };
    if (userRole === "CustomerAdmin") {
      const tenantIdFromStorage = localStorage.getItem("tenantId");
      if (!tenantIdFromStorage || tenantIdFromStorage === "null") {
        alert("CustomerAdmin must have a tenantId to create users.");
        return;
      }
      submissionData.tenantId = tenantIdFromStorage;
    } else if (userRole === "SuperAdmin" && !submissionData.tenantId) {
      alert("Please select an organization for the user.");
      return;
    }

    try {
      if (isEditing) {
        await updateUser(selectedUserId, submissionData);
      } else {
        await addUser(submissionData);
      }
      fetchUsers();
      setOpen(false);
      resetForm();
    } catch (err) {
      console.error("Failed to submit form:", err);
    }
  };

  const handleEdit = (user) => {
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      mobile: user.mobile,
      tenantRoles: user.tenantRoles || [],
      username: user.username || "",
      password: "",
      isLoginEnabled: user.isLoginEnabled || false,
      tenantId: user.tenantId || (userRole === "CustomerAdmin" ? userTenantId : ""),
    });
    setSelectedUserId(user._id);
    setIsEditing(true);
    setOpen(true);

    // Fetch roles for the user's tenantId when editing
    if (userRole === "SuperAdmin" && user.tenantId) {
      fetchAvailableRoles(user.tenantId);
    } else if (userRole === "CustomerAdmin" && userTenantId) {
      fetchAvailableRoles(userTenantId);
    }
  };

  const handleDelete = async (userId) => {
    if (!userId) return;
    await deleteUser(userId);
    fetchUsers();
    setOpen(false);
  };

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      mobile: "",
      tenantRoles: [],
      username: "",
      password: "",
      isLoginEnabled: false,
      tenantId: userRole === "CustomerAdmin" ? userTenantId : "",
    });
    setIsEditing(false);
    setSelectedUserId(null);
    clearError();
    // Reset roles for SuperAdmin
    if (userRole === "SuperAdmin") {
      fetchAvailableRoles("");
    }
  };

  const columnDefs = [
    {
      headerName: "First Name",
      headerStyle: { backgroundColor: "#e8eff3" },
      field: "firstName",
      flex: 0.5,
    },
    {
      headerName: "Last Name",
      headerStyle: { backgroundColor: "#e8eff3" },
      field: "lastName",
      flex: 0.5,
    },
    {
      headerName: "Email",
      headerStyle: { backgroundColor: "#e8eff3" },
      field: "email",
      flex: 0.5,
    },
    {
      headerName: "Mobile",
      headerStyle: { backgroundColor: "#e8eff3" },
      field: "mobile",
      flex: 0.5,
    },
    {
      headerName: "Username",
      headerStyle: { backgroundColor: "#e8eff3" },
      field: "username",
      flex: 0.5,
    },
    {
      headerName: "Roles",
      headerStyle: { backgroundColor: "#e8eff3" },
      field: "tenantRoles",
      flex: 0.5,
      cellRenderer: (params) => (
        <div className="flex space-x-2">
          {(params.data.tenantRoles || []).map((role, index) => (
            <div key={index} className="flex items-center space-x-1">
              <input type="checkbox" checked={true} readOnly />
              {role}
            </div>
          ))}
        </div>
      ),
    },
    {
      headerName: "Action",
      field: "action",
      headerStyle: { backgroundColor: "#e8eff3" },
      flex: 0.5,
      cellRenderer: (params) => (
        <div className="flex space-x-2">
          <button
            className="text-blue-500 hover:text-blue-700"
            onClick={() => handleEdit(params.data)}
          >
            <FiEdit size={18} />
          </button>
          <button
            className="text-red-500 hover:text-red-700"
            onClick={() => handleDelete(params.data._id)}
          >
            <RiDeleteBin6Line size={18} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 mt-10 w-full">
      <h1 className="text-2xl font-bold mb-4">USER MANAGEMENT</h1>
      <div className="mt-4 mb-4 flex justify-between items-center">
        <button
          className="flex text-md items-center gap-2 bg-[#a9e5f7] text-[#074659] font-bold px-4 py-2 rounded-full"
          onClick={() => {
            resetForm();
            setOpen(true);
          }}
        >
          <PlusIcon size={30} />
          CREATE USER
        </button>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={20} className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search By Name"
            value={searchTerm}
            onChange={handleSearch}
            className="pl-10 pr-20 py-2 w-full border border-[#bcc9db] placeholder:text-[#6f7987] placeholder:text-sm placeholder:font-semibold rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      {error && <div className="text-red-500 mb-4">{error}</div>}
      <div className="ag-theme-alpine-customer w-full h-full">
        <AgGridReact
          rowData={users}
          rowHeight={50}
          headerHeight={50}
          pagination={true}
          paginationPageSize={10}
          columnDefs={columnDefs}
          domLayout="autoHeight"
        />
      </div>

      <Modal isOpen={open}>
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <div className="w-[1000px]">
              <div className="flex flex-col">
                <h1 className="font-bold text-lg">
                  {isEditing ? "EDIT USER" : "CREATE USER"}
                </h1>
                <hr className="w-full border border-[#bcc9db] mt-2" />
              </div>

              {error && <div className="text-red-500 mt-4">{error}</div>}

              <div className="grid grid-cols-3 gap-4">
                {userRole === "SuperAdmin" && (
                  <div className="mt-5 flex flex-col">
                    <label
                      htmlFor="tenantId"
                      className="font-semibold text-[#949494] text-sm"
                    >
                      Organization
                    </label>
                    <select
                      id="tenantId"
                      name="tenantId"
                      value={tenantId}
                      onChange={handleInputChange}
                      className="w-full border border-[#bcc9db] rounded-sm px-2 py-1 mt-1"
                      required
                    >
                      <option value="">Select Organization</option>
                      {customers.map((customer) => (
                        <option key={customer._id} value={customer._id}>
                          {customer.nameOfOrganization}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="mt-5 flex flex-col">
                  <label
                    htmlFor="firstName"
                    className="font-semibold text-[#949494] text-sm"
                  >
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={firstName}
                    onChange={handleInputChange}
                    placeholder="Enter First Name"
                    className="w-full border border-[#bcc9db] rounded-sm px-2 py-1 mt-1 placeholder:text-black"
                    required
                  />
                </div>
                <div className="mt-5 flex flex-col">
                  <label
                    htmlFor="lastName"
                    className="font-semibold text-[#949494] text-sm"
                  >
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={lastName}
                    onChange={handleInputChange}
                    placeholder="Enter Last Name"
                    className="w-full border border-[#bcc9db] rounded-sm px-2 py-1 mt-1 placeholder:text-black"
                    required
                  />
                </div>
                <div className="mt-5 flex flex-col">
                  <label
                    htmlFor="email"
                    className="font-semibold text-[#949494] text-sm"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={handleInputChange}
                    placeholder="Enter Email"
                    className="w-full border border-[#bcc9db] rounded-sm px-2 py-1 mt-1 placeholder:text-black"
                    required
                  />
                </div>
                <div className="mt-5 flex flex-col">
                  <label
                    htmlFor="mobile"
                    className="font-semibold text-[#949494] text-sm"
                  >
                    Mobile
                  </label>
                  <input
                    type="text"
                    id="mobile"
                    name="mobile"
                    value={mobile}
                    onChange={handleInputChange}
                    placeholder="Enter Mobile Number"
                    className="w-full border border-[#bcc9db] rounded-sm px-2 py-1 mt-1 placeholder:text-black"
                  />
                </div>
                <div className="mt-5 flex flex-col">
                  <label
                    htmlFor="username"
                    className="font-semibold text-[#949494] text-sm"
                  >
                    Username
                  </label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={username}
                    onChange={handleInputChange}
                    placeholder="Enter Username"
                    className="w-full border border-[#bcc9db] rounded-sm px-2 py-1 mt-1 placeholder:text-black"
                    disabled={!isLoginEnabled}
                    required={isLoginEnabled}
                  />
                </div>
                <div className="mt-5 flex flex-col">
                  <label
                    htmlFor="password"
                    className="font-semibold text-[#949494] text-sm"
                  >
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={password}
                    onChange={handleInputChange}
                    placeholder="Enter Password"
                    className="w-full border border-[#bcc9db] rounded-sm px-2 py-1 mt-1 placeholder:text-black"
                    disabled={!isLoginEnabled}
                    required={isLoginEnabled && !isEditing}
                  />
                </div>
                <div className="mt-5 flex items-center">
                  <input
                    type="checkbox"
                    id="isLoginEnabled"
                    name="isLoginEnabled"
                    checked={isLoginEnabled}
                    onChange={handleInputChange}
                    className="w-4 h-4"
                  />
                  <label
                    htmlFor="isLoginEnabled"
                    className="ml-2 font-semibold text-[#949494] text-sm"
                  >
                    Enable Login
                  </label>
                </div>
                <div className="mt-5">
                  <label className="font-semibold text-[#949494] text-sm">
                    Roles
                  </label>
                  <div className="flex gap-3 mt-2 flex-wrap">
                    {availableRoles.map((roleItem) => (
                      <label
                        key={roleItem}
                        className="flex items-center space-x-2"
                      >
                        <input
                          type="checkbox"
                          name="tenantRoles"
                          value={roleItem}
                          checked={tenantRoles.includes(roleItem)}
                          onChange={handleInputChange}
                          className="w-4 h-4"
                        />
                        <span>{roleItem}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 bg-[#f5fdff] text-[#ffffff] mt-10 py-3 px-3">
            <button
              type="button"
              className="bg-[#7e90ab] px-5 py-1 rounded-full"
              onClick={() => setOpen(false)}
            >
              CANCEL
            </button>
            <button type="submit" className="bg-[#063269] px-5 py-1 rounded-full">
              SUBMIT
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default UserManagement;